import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import { ArrowOutward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { useAuthStore } from '../stores/authStore';
import { createAppointmentRequest } from '../services/requestsService';
import { BackButton } from '../components/common/BackButton';
import { SuccessPopup } from '../components/common/SuccessPopup';

// Основной компонент
export const AppointmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branches, clinic } = useClinic();
  const { user: authUser } = useAuthStore();

  const currentBranch = useMemo(() => branches.find((b) => b.id === authUser?.branchId), [branches, authUser?.branchId]);
  const clinicName = currentBranch?.name || clinic?.name || 'клиники';
  const userName = authUser?.name || 'пользователь';

  const workSchedule = useMemo(() => {
    if (currentBranch?.workSchedule) {
      return currentBranch.workSchedule;
    }
    return null;
  }, [currentBranch?.workSchedule]);

  const [screen, setScreen] = useState<'selection' | 'managerForm'>('selection');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Manager form fields
  const [formData, setFormData] = useState({
    doctorName: '',
    serviceName: '',
  });

  // Онлайн запись
  const handleOnlineBooking = () => {
    if (clinic?.appointmentLink) {
      window.open(clinic.appointmentLink, '_blank');
    }
  };

  // Запись через менеджера
  const handleManagerSubmit = async () => {
    if (!formData.doctorName.trim() || !formData.serviceName.trim()) {
      return;
    }

    setLoading(true);
    try {
      await createAppointmentRequest({ from: 'webapp' });
      setShowSuccessModal(true);
      setFormData({ doctorName: '', serviceName: '' });
    } catch (err) {
      console.error('Ошибка при отправке заявки:', err);
    } finally {
      setLoading(false);
    }
  };

  // Экран выбора - ДВЕ ПЛИТКИ
  if (screen === 'selection') {
    return (
      <AppLayout title="Записаться">
        <Box sx={{ pb: 8, px: 2 }}>
          <BackButton onClick={() => navigate(-1)} />

          <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 700, color: '#000', mb: 1, lineHeight: 1.1 }}>
            Запись
          </Typography>

          {/* Контейнер для центрирования плиток */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: '16px',
            }}
          >
            {/* ПЛИТКА 1: ЗАПИСАТЬСЯ ОНЛАЙН */}
            <Box
              onClick={clinic?.appointmentLink ? handleOnlineBooking : undefined}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '359px', md: '480px' },
                height: { xs: 200, sm: 240, md: 280 },
                padding: { xs: '16px', sm: '20px', md: '24px' },
                borderRadius: '28px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                cursor: clinic?.appointmentLink ? 'pointer' : 'not-allowed',
                opacity: clinic?.appointmentLink ? 1 : 0.6,
                transition: 'background-color 0.2s',
                '&:active': {
                  backgroundColor: clinic?.appointmentLink ? 'rgba(0,0,0,0.03)' : '#FFFFFF',
                },
                flexShrink: 0,
              }}
            >
              {/* Заголовок чуть ниже верхнего левого угла */}
              <Typography
                sx={{
                  fontSize: { xs: 22, sm: 28, md: 32 },
                  fontWeight: 500,
                  color: '#000',
                  lineHeight: 1.2,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  maxWidth: '280px',
                  mt: 1.5,
                }}
              >
                Записаться онлайн
              </Typography>

              {/* Иконка чуть ниже верхнего правого угла */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 16, sm: 20, md: 24 },
                  right: { xs: 16, sm: 20, md: 24 },
                  width: { xs: 36, sm: 40, md: 48 },
                  height: { xs: 36, sm: 40, md: 48 },
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  border: '1.5px solid #007AFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ArrowOutward sx={{ fontSize: { xs: 18, sm: 20, md: 24 }, color: '#007AFF' }} />
              </Box>

              {/* Текст внизу */}
              <Typography
                sx={{
                  fontSize: { xs: 13, sm: 16, md: 18 },
                  fontWeight: 500,
                  color: '#8E8E93',
                  lineHeight: 1.25,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                Для онлайн записи необходимо перейти на сайт
              </Typography>
            </Box>

            {/* ПЛИТКА 2: ЗАПИСАТЬСЯ ЧЕРЕЗ МЕНЕДЖЕРА (PRIMARY) */}
            <Box
              onClick={() => setScreen('managerForm')}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '359px', md: '480px' },
                height: { xs: 200, sm: 240, md: 280 },
                padding: { xs: '16px', sm: '20px', md: '24px' },
                borderRadius: '28px',
                backgroundColor: '#007AFF',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 6px 20px rgba(0,122,255,0.25)',
                '&:active': {
                  transform: 'scale(0.97)',
                },
                flexShrink: 0,
              }}
            >
              {/* Заголовок чуть ниже верхнего левого угла */}
              <Typography
                sx={{
                  fontSize: { xs: 22, sm: 28, md: 32 },
                  fontWeight: 500,
                  color: '#FFFFFF',
                  lineHeight: 1.2,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  maxWidth: '280px',
                  mt: 1.5,
                }}
              >
                Записаться через менеджера
              </Typography>

              {/* Иконка чуть ниже верхнего правого угла */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 16, sm: 20, md: 24 },
                  right: { xs: 16, sm: 20, md: 24 },
                  width: { xs: 36, sm: 40, md: 48 },
                  height: { xs: 36, sm: 40, md: 48 },
                  borderRadius: '50%',
                  backgroundColor: '#007AFF',
                  border: '1.5px solid #FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ArrowOutward sx={{ fontSize: { xs: 18, sm: 20, md: 24 }, color: '#FFFFFF' }} />
              </Box>

              {/* Текст внизу */}
              <Typography
                sx={{
                  fontSize: { xs: 13, sm: 16, md: 18 },
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.25,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                Нажмите, чтобы записаться ко врачу через менеджера
              </Typography>
            </Box>
          </Box>
        </Box>
      </AppLayout>
    );
  }

  // Форма экрана менеджера
if (screen === 'managerForm') {
  const isFormFilled = formData.doctorName.trim() && formData.serviceName.trim();

  return (
    <AppLayout title="Запись через менеджера">
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        px: 2,
        pb: 2,
      }}>
        <BackButton onClick={() => setScreen('selection')} />

        <Typography sx={{
          fontSize: { xs: '28px', sm: '32px' },
          fontWeight: 700,
          color: '#1C1C1E',
          mb: '16px',
          lineHeight: 1.1,
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          Запись через менеджера
        </Typography>

        <Typography sx={{
          fontSize: 14,
          color: '#6D6D72',
          mb: '16px', // 👈 УМЕНЬШИЛ: было '24px'
          lineHeight: 1.43,
          fontWeight: 400,
        }}>
          {userName}, напишите, пожалуйста, к какому врачу или на какую услугу вы бы хотели записаться
        </Typography>

        {/* Doctor Name Input */}
        <TextField
          fullWidth
          placeholder="ФИО врача"
          value={formData.doctorName}
          onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
          inputProps={{
            style: {
              fontSize: '15px',
              fontWeight: 400,
            }
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              height: '52px',
              borderRadius: '26px',
              backgroundColor: '#F2F2F7',
              padding: '0 16px',
              '& fieldset': {
                borderColor: '#D1D1D6',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: '#D1D1D6',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#007AFF',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#8E8E93',
              opacity: 1,
            },
          }}
        />

        {/* Service Name Input */}
        <TextField
          fullWidth
          placeholder="Название услуги"
          value={formData.serviceName}
          onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
          inputProps={{
            style: {
              fontSize: '15px',
              fontWeight: 400,
            }
          }}
          sx={{
            mb: 'auto',
            '& .MuiOutlinedInput-root': {
              height: '52px',
              borderRadius: '26px',
              backgroundColor: '#F2F2F7',
              padding: '0 16px',
              '& fieldset': {
                borderColor: '#D1D1D6',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: '#D1D1D6',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#007AFF',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#8E8E93',
              opacity: 1,
            },
          }}
        />

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          disabled={!isFormFilled || loading}
          onClick={handleManagerSubmit}
          sx={{
            height: '52px', // 👈 УВЕЛИЧИЛ: было 52px, теперь повыше
            borderRadius: '28px', // 👈 ИЗМЕНИЛ: чтобы соответствовать новой высоте
            fontWeight: 600,
            fontSize: 16,
            textTransform: 'none',
            boxShadow: 'none',
            backgroundColor: isFormFilled ? '#007AFF' : '#8FB1D6',
            color: '#FFFFFF',
            transition: 'background-color 0.2s, opacity 0.2s, transform 0.15s',
            opacity: isFormFilled ? 1 : 0.8,
            mb: 12,
            '&:active': {
              transform: isFormFilled ? 'scale(0.97)' : 'scale(1)',
            },
            '&:hover': {
              backgroundColor: isFormFilled ? '#005FCC' : '#8FB1D6',
            },
            '&:disabled': {
              backgroundColor: '#8FB1D6',
              color: '#FFFFFF',
              opacity: 0.8,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#FFF' }} />
          ) : (
            'Отправить'
          )}
        </Button>

        {/* Success Modal */}
        <SuccessPopup
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setScreen('selection');
          }}
          clinicName={clinicName}
          workSchedule={workSchedule}
        />
      </Box>
    </AppLayout>
  );
}

  return null;
};
